"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";

import { cn } from "@/shared/lib/utils/cn";
import {
  deleteInsightsThread,
  INSIGHTS_THREADS_CHANGED_EVENT,
  listInsightsThreads,
} from "../history/insights-history";
import type { InsightsHistoryEntry } from "../types";

export function InsightsSidebarList({ collapsed }: { collapsed: boolean }) {
  const pathname = usePathname();
  const router = useRouter();
  const [threads, setThreads] = useState<InsightsHistoryEntry[]>(() =>
    typeof window === "undefined" ? [] : listInsightsThreads(),
  );

  useEffect(() => {
    const sync = () => setThreads(listInsightsThreads());
    const onStorage = () => sync();
    window.addEventListener("storage", onStorage);
    window.addEventListener(INSIGHTS_THREADS_CHANGED_EVENT, sync);
    const interval = window.setInterval(sync, 2000);
    const raf = window.requestAnimationFrame(sync);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(INSIGHTS_THREADS_CHANGED_EVENT, sync);
      window.clearInterval(interval);
      window.cancelAnimationFrame(raf);
    };
  }, [pathname]);

  if (collapsed) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col px-2 pb-2">
      <div className="mb-1 px-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Investigations
      </div>
      <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
        {threads.length === 0 ? (
          <p className="px-2 py-2 text-[12px] text-muted-foreground">No insights threads yet.</p>
        ) : (
          threads.map((t) => {
            const active = pathname.includes(t.threadId);
            return (
              <div key={t.threadId} className="group relative">
                <Link
                  href={`/insights/${t.threadId}`}
                  className={cn(
                    "block truncate rounded-lg px-3 py-2 text-[13px]",
                    active
                      ? "bg-accent text-primary"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                  )}
                >
                  {t.title}
                </Link>
                <button
                  type="button"
                  className="absolute right-1 top-1/2 hidden -translate-y-1/2 rounded px-1.5 text-[11px] text-muted-foreground hover:bg-background hover:text-destructive group-hover:block"
                  onClick={(e) => {
                    e.preventDefault();
                    deleteInsightsThread(t.threadId);
                    setThreads(listInsightsThreads());
                    if (active) router.push("/insights");
                  }}
                >
                  ×
                </button>
              </div>
            );
          })
        )}
      </div>
      <Link
        href="/insights"
        className="mt-1 rounded-lg px-3 py-2 text-[13px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground"
      >
        New investigation
      </Link>
    </div>
  );
}
